import { useQuery, UseQueryResult } from 'react-query'
import { useSolana } from '../providers/solana'
import { getProtocolInfo, ProtocolV2StakingInfo, SolCatStakingProtocolInfo } from '@catsolhat/staking-sdk'
import { PublicKey } from '@solana/web3.js'
import { isSolcatTokenAddress } from '../utils/helpers'

export const useProtocolData = (tokenAddresses: string[] = []): UseQueryResult<ProtocolV2StakingInfo, Error> => {
  const { connection } = useSolana()

  const fetchProtocolData = async () => {
    if (!connection || tokenAddresses.length === 0) return

    const solcatTokens = tokenAddresses.filter(isSolcatTokenAddress)
    const regularTokens = tokenAddresses.filter((address) => !isSolcatTokenAddress(address))

    const v1Result =
      solcatTokens.length > 0
        ? await getProtocolInfo(
            connection,
            false,
            solcatTokens.map((address) => new PublicKey(address)),
          )
        : (null as SolCatStakingProtocolInfo | null)

    const v2Result =
      regularTokens.length > 0
        ? await getProtocolInfo(
            connection,
            true,
            regularTokens.map((address) => new PublicKey(address)),
          )
        : null

    const v1ResultTransformed = v1Result ? transformV1ToV2Format(v1Result as SolCatStakingProtocolInfo) : null
    const result = {
      admin: v2Result?.admin,
      stakingInfo: {
        ...(v2Result as ProtocolV2StakingInfo)?.stakingInfo,
        ...v1ResultTransformed?.stakingInfo,
      },
    }
    return result as ProtocolV2StakingInfo
  }

  const tokenCacheKey = tokenAddresses.length > 0 ? tokenAddresses.join(',') : 'all_tokens'
  return useQuery(['protocolBasicInfo', tokenCacheKey], fetchProtocolData, {
    enabled: !!connection,
  })
}

const transformV1ToV2Format = (v1Data: SolCatStakingProtocolInfo): ProtocolV2StakingInfo => {
  return {
    admin: v1Data.admin,
    stakingInfo: {
      [v1Data.mint]: {
        flexibleRewardRate: v1Data.flexibleRewardRate,
        flexibleApr: v1Data.flexibleApr,
        flexibleWithdrawalDelayTime: v1Data.flexibleWithdrawalDelayTime,
        lockupApy: v1Data.lockupApy,
        lockupDuration: v1Data.lockupDuration,
        minLockupAmount: v1Data.minLockupAmount,
      },
    },
  }
}
